import { IProductPricing, IPriceModel } from "../types/Product";

export const addVat = (amount: number, vat: number = 0) =>
  Number((amount * (1 + vat / 100)).toFixed(2));
export const removeVat = (amount: number, vat: number = 0) =>
  Number((amount / (1 + vat / 100)).toFixed(2));

export const getUnitPrice = (
  pricing: IProductPricing | undefined,
  model: IPriceModel,
): number => {
  if (!pricing) return 0;
  let raw: number | undefined;
  switch (model) {
    case IPriceModel.SEAT:
      raw = pricing.pricePerSeat;
      break;
    case IPriceModel.FIXED:
      raw = pricing.pricePerCargoUnit;
      break;
    case IPriceModel.DISTANCE:
      raw = pricing.pricePerDistanceUnit;
      break;
    case IPriceModel.VOLUME:
      raw = pricing.pricePerVolumeUnit;
      break;
    case IPriceModel.WEIGHT:
      raw = pricing.pricePerWeightUnit;
      break;
    case IPriceModel.TIME:
      raw = pricing.pricePerTimeUnit;
      break;
    default:
      raw = 0;
  }
  if (!raw) return 0;
  return pricing.pricesIncludeVat && model !== IPriceModel.SEAT
    ? raw
    : addVat(raw, pricing.vat ?? 0);
};

export const totalForQuantity = (unit: number, quantity: number | undefined) =>
  Number((unit * (quantity ?? 1)).toFixed(2));

/**
 * Time-price invoice lines arrive from the backend described as
 * "Tidspris (name)" or "Time price (name)". Their quantity (and therefore
 * amount) is only known once the order is completed, so callers render them as
 * "to be calculated" rather than multiplying by a fabricated quantity of 1.
 */
export const isTimePriceDescription = (description?: string): boolean => {
  if (!description) return false;
  const normalized = description.trim().toLowerCase();
  return (
    normalized.startsWith("tidspris") || normalized.startsWith("time price")
  );
};

/**
 * Determine if the company associated with a driver is VAT-registered.
 *
 * @param driverId The driver identifier (typically email).
 * @param drivers  Array of Driver objects (must contain at least companyId field).
 * @param companies Array of Company objects (must contain id and paymentTerms.isVatRegistered).
 * @returns boolean indicating VAT registration, or false if not determinable.
 */
export const isDriverCompanyVatRegistered = (
  driverId: string | undefined,
  drivers: { email?: string; companyId?: string }[] | undefined,
  companies:
    { id?: string; paymentTerms?: { isVatRegistered?: boolean } }[] | undefined,
): boolean => {
  if (!driverId || !drivers || !companies) return false;

  const driver = drivers.find((d) => d.email === driverId);
  if (!driver || !driver.companyId) return false;

  const company = companies.find((c) => c.id === driver.companyId);
  return company?.paymentTerms?.isVatRegistered ?? false;
};
